import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Lock, Megaphone, UserCheck } from "lucide-react";
import { Team } from "util/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/InputSelect";
import {
    PrivacyLevel,
    TeamInboxMode,
    updateTeamAllowContentPromotion,
    updateTeamDefaultAudience,
    updateTeamMessageInboxMode,
    updateTeamMessagePrivacy,
    updateTeamPrivateStatus,
} from "util/teamUtils";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

interface Props {
    team: Team;
}

const TeamSettingsPrivacy: React.FC<Props> = ({ team }) => {
    const [isPrivate, setIsPrivate] = useState<boolean>(false);
    const [defaultAudience, setDefaultAudience] = useState<PrivacyLevel>(team.properties_public?.is_private ? "follow" : "public");
    const [allowPublic, setAllowPublic] = useState<boolean>(true);
    const [allowFollowers, setAllowFollowers] = useState<boolean>(false);
    const [allowNoOne, setAllowNoOne] = useState<boolean>(false);
    const [inboxMode, setInboxMode] = useState<TeamInboxMode>("moderators_only");
    const [allowPromotion, setAllowPromotion] = useState<boolean>(false);
    const [isUpdatingPromotion, setIsUpdatingPromotion] = useState<boolean>(false);
    const storyStrings = getStoryUiStrings(resolveStoryLocale());

    useEffect(() => {
        const isTeamPrivate = team.properties_public?.is_private === undefined ? false : team.properties_public?.is_private;
        setIsPrivate(isTeamPrivate);

        const savedAudience = team.properties?.privacy_content_default_audience.value ? team.properties?.privacy_content_default_audience.value : team.properties_public?.is_private ? "follow" : "public";
        setDefaultAudience(savedAudience as PrivacyLevel);

        const messageSettings = team.properties?.privacy_message_receive?.value || ["public"];
        setAllowPublic(messageSettings.includes("public"));
        setAllowFollowers(messageSettings.includes("followers") || messageSettings.includes("follow"));
        setAllowNoOne(messageSettings.includes("no_one"));

        const savedInboxMode = team.properties?.privacy_message_inbox_mode?.value;
        setInboxMode(savedInboxMode === "all_members" ? "all_members" : "moderators_only");

        const storedAllowPromotion = (team.properties as { allow_content_promotion?: { value?: boolean } } | null | undefined)
            ?.allow_content_promotion?.value;
        setAllowPromotion(
            typeof storedAllowPromotion === "boolean" ? storedAllowPromotion : !isTeamPrivate
        );
    }, [team]);

    const handleAllowPromotionChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        setAllowPromotion(nextValue);
        setIsUpdatingPromotion(true);
        try {
            await updateTeamAllowContentPromotion(team.id, nextValue);
            if (team) {
                const properties = (team.properties || {}) as Record<string, { value: unknown }>;
                properties.allow_content_promotion = { value: nextValue };
                team.properties = properties as typeof team.properties;
            }
        } catch (error) {
            console.error("Error updating team allow_content_promotion:", error);
            toast.error(storyStrings.promoteError);
            setAllowPromotion(!nextValue);
        } finally {
            setIsUpdatingPromotion(false);
        }
    };

    const handlePrivateAccountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked;
        try {
            const response = await updateTeamPrivateStatus(team.id, value);
            setIsPrivate(response.data.properties_public?.is_private ?? false);
            if (team) {
                team.properties_public = {
                    ...team.properties_public,
                    is_private: value
                };
            }
        } catch (error) {
            console.error("Error updating privacy settings:", error);
        }
    };

    const persistMessagePrivacy = async (nextPublic: boolean, nextFollowers: boolean, nextNoOne: boolean): Promise<void> => {
        const nextValues: Array<"public" | "followers" | "no_one"> = [];
        if (nextNoOne) {
            nextValues.push("no_one");
        } else {
            if (nextPublic) nextValues.push("public");
            if (nextFollowers) nextValues.push("followers");
        }
        await updateTeamMessagePrivacy(team.id, nextValues);
        if (team) {
            team.properties.privacy_message_receive.value = nextValues;
        }
    };

    const handleMessagePrivacyChange = async (type: "public" | "followers" | "no_one", checked: boolean) => {
        try {
            const nextPublic = type === "public" ? checked : (type === "no_one" && checked ? false : allowPublic);
            const nextFollowers = type === "followers" ? checked : (type === "no_one" && checked ? false : allowFollowers);
            const nextNoOne = type === "no_one" ? checked : allowNoOne;

            setAllowPublic(nextPublic);
            setAllowFollowers(nextFollowers);
            setAllowNoOne(nextNoOne);

            await persistMessagePrivacy(nextPublic, nextFollowers, nextNoOne);
        } catch (error) {
            console.error("Error updating message privacy:", error);
        }
    };

    const handleDefaultAudienceChange = async (value: PrivacyLevel) => {
        try {
            await updateTeamDefaultAudience(team.id, value);
            setDefaultAudience(value);
            if (team) {
                team.properties.privacy_content_default_audience.value = value;
            }
        } catch (error) {
            console.error("Error updating default audience:", error);
        }
    };

    const handleInboxModeChange = async (value: TeamInboxMode) => {
        try {
            await updateTeamMessageInboxMode(team.id, value);
            setInboxMode(value);
            if (team) {
                team.properties.privacy_message_inbox_mode = { value };
            }
        } catch (error) {
            console.error("Error updating inbox mode:", error);
        }
    };

    const privacyOptions = [
        { value: "members", label: "Partea Members", icon: Lock },
        { value: "follow", label: "Followers", icon: UserCheck },
        { value: "public", label: "Public", icon: Globe },
    ];

    return (
        <div className="p-2">
            <div className="space-y-2">
                <div className="form-control space-y-2">
                    <label className="form-label text-base">Interactions</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Private Partea</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                When enabled, approval is required to follow your partea.
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={isPrivate}
                            onChange={handlePrivateAccountChange}
                            className="toggle-switch-input col-span-1 flex-shrink-0"
                        />
                    </div>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Messages</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose who can message your partea.
                            </p>
                            <div className="w-full space-y-3">
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Globe className="size-4" /> Public</span>
                                    <input
                                        type="checkbox"
                                        checked={allowPublic}
                                        onChange={(e) => handleMessagePrivacyChange("public", e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><UserCheck className="size-4" /> Followers</span>
                                    <input
                                        type="checkbox"
                                        checked={allowFollowers}
                                        onChange={(e) => handleMessagePrivacyChange("followers", e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <span className="flex flex-row items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm"><Lock className="size-4" /> No one</span>
                                    <input
                                        type="checkbox"
                                        checked={allowNoOne}
                                        onChange={(e) => handleMessagePrivacyChange("no_one", e.target.checked)}
                                        className="toggle-switch-input col-span-1 flex-shrink-0"
                                    />
                                </div>
                                <div className="rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main p-3 space-y-2">
                                    <div className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
                                        When non-members message this partea
                                    </div>
                                    <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        Decide whether outsider conversations include all members or only moderators by default.
                                    </p>
                                    <Select onValueChange={(value) => handleInboxModeChange(value as TeamInboxMode)} value={inboxMode}>
                                        <SelectTrigger className="input-select px-3 py-1 rounded-md">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                                            <SelectItem value="moderators_only">Moderators only</SelectItem>
                                            <SelectItem value="all_members">All members</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="form-control space-y-2">
                    <label className="form-label text-base">Content</label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">Default audience</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub mb-4">
                                Choose who can see your partea's content by default; you can modify this on a per-post basis.
                            </p>
                            <Select onValueChange={handleDefaultAudienceChange} value={defaultAudience}>
                                <SelectTrigger className="input-select px-3 py-1 rounded-md">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                                    {privacyOptions.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                                <option.icon className="size-4" />
                                                <span>{option.label}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <div className="form-control space-y-2">
                    <label className="form-label text-base flex items-center gap-2">
                        <Megaphone className="size-4" aria-hidden="true" />
                        Ad breaks
                    </label>
                    <div className="group flex justify-between items-center p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
                        <div className="flex flex-col items-start flex-grow">
                            <span className="text-eoe-text dark:text-eoe-dark-text text-sm">{storyStrings.allowPromotionToggleLabel}</span>
                            <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                                {storyStrings.allowPromotionToggleDescription}
                            </p>
                        </div>
                        <input
                            type="checkbox"
                            checked={allowPromotion}
                            disabled={isUpdatingPromotion}
                            onChange={handleAllowPromotionChange}
                            className="toggle-switch-input col-span-1 flex-shrink-0"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TeamSettingsPrivacy 